/**
 * Dimension Presets for GridMaster
 * 
 * Standard paper sizes and screen dimensions for quick selection
 */

export const PRESET_CATEGORIES = {
  PRINT: {
    A_SERIES: 'A-Series',
    US_STANDARD: 'US Standard',
    DESIGN_FORMATS: 'Design Formats'
  },
  DIGITAL: {
    DESKTOP: 'Desktop',
    WEB_DESIGN: 'Web Design', 
    MOBILE: 'Mobile',
    TABLET: 'Tablet'
  }
};

/**
 * Base point presets - Print and paper formats
 * All dimensions in points (1 point = 1/72 inch)
 * Portrait orientation (width < height) as base, landscape computed dynamically
 */
const POINT_BASE_PRESETS = [
  // A-Series (ISO 216) - Portrait as base, exact Illustrator dimensions
  {
    baseId: 'a6',
    name: 'A6',
    width: 297.64,  // 105mm = 297.638pt
    height: 419.53, // 148mm = 419.528pt
    category: PRESET_CATEGORIES.PRINT.A_SERIES,
    description: '105 × 148 mm',
    hasOrientations: true
  },
  {
    baseId: 'a5',
    name: 'A5',
    width: 419.53,  // 148mm = 419.528pt
    height: 595.28, // 210mm = 595.276pt
    category: PRESET_CATEGORIES.PRINT.A_SERIES,
    description: '148 × 210 mm',
    hasOrientations: true
  },
  {
    baseId: 'a4',
    name: 'A4',
    width: 595.28,  // 210mm = 595.276pt
    height: 841.89, // 297mm = 841.890pt
    category: PRESET_CATEGORIES.PRINT.A_SERIES,
    description: '210 × 297 mm',
    hasOrientations: true
  },
  {
    baseId: 'a3',
    name: 'A3',
    width: 841.89,  // 297mm = 841.890pt
    height: 1190.55, // 420mm = 1190.551pt
    category: PRESET_CATEGORIES.PRINT.A_SERIES,
    description: '297 × 420 mm',
    hasOrientations: true
  },

  // US Standard (ANSI) - Portrait as base, exact Illustrator dimensions
  {
    baseId: 'us-letter',
    name: 'US Letter',
    width: 612,     // 8.5" = 612pt (exact)
    height: 792,    // 11" = 792pt (exact)
    category: PRESET_CATEGORIES.PRINT.US_STANDARD,
    description: '8.5 × 11 inches',
    hasOrientations: true
  },
  {
    baseId: 'us-legal',
    name: 'US Legal',
    width: 612,     // 8.5" = 612pt (exact)
    height: 1008,   // 14" = 1008pt (exact)
    category: PRESET_CATEGORIES.PRINT.US_STANDARD,
    description: '8.5 × 14 inches',
    hasOrientations: true
  },
  {
    baseId: 'us-tabloid',
    name: 'US Tabloid',
    width: 792,     // 11" = 792pt (exact)
    height: 1224,   // 17" = 1224pt (exact)
    category: PRESET_CATEGORIES.PRINT.US_STANDARD,
    description: '11 × 17 inches',
    hasOrientations: true
  },

  // Design Formats - Exact Illustrator dimensions
  {
    baseId: 'business-card',
    name: 'Business Card',
    width: 144,     // 2" = 144pt (exact)
    height: 252,    // 3.5" = 252pt (exact)
    category: PRESET_CATEGORIES.PRINT.DESIGN_FORMATS,
    description: '2 × 3.5 inches',
    hasOrientations: true
  },
  {
    baseId: 'postcard',
    name: 'Postcard',
    width: 252,     // 3.5" = 252pt (exact)
    height: 360,    // 5" = 360pt (exact)
    category: PRESET_CATEGORIES.PRINT.DESIGN_FORMATS,
    description: '3.5 × 5 inches',
    hasOrientations: true
  }
];

/**
 * Base pixel presets - Digital and screen formats
 * All dimensions in pixels for web and app design
 * Most digital screens are naturally landscape, mobile devices portrait
 */
const PIXEL_BASE_PRESETS = [
  // Desktop Screens (landscape orientation as base)
  {
    baseId: 'desktop-4k',
    name: 'Desktop 4K',
    width: 3840,
    height: 2160,
    category: PRESET_CATEGORIES.DIGITAL.DESKTOP,
    description: 'Ultra HD - 4K resolution',
    hasOrientations: false // Desktop screens typically don't rotate
  },
  {
    baseId: 'desktop-qhd',
    name: 'Desktop QHD',
    width: 2560,
    height: 1440,
    category: PRESET_CATEGORIES.DIGITAL.DESKTOP,
    description: '1440p - Quad HD resolution',
    hasOrientations: false
  },
  {
    baseId: 'desktop-hd',
    name: 'Desktop HD',
    width: 1920,
    height: 1080,
    category: PRESET_CATEGORIES.DIGITAL.DESKTOP,
    description: '1080p - Full HD resolution',
    hasOrientations: false
  },
  {
    baseId: 'macbook-pro-16',
    name: 'MacBook Pro 16"',
    width: 3456,
    height: 2234,
    category: PRESET_CATEGORIES.DIGITAL.DESKTOP,
    description: 'MacBook Pro 16-inch display',
    hasOrientations: false
  },

  // Web Design Breakpoints (landscape as base)
  {
    baseId: 'web-large',
    name: 'Web Large',
    width: 1440,
    height: 900,
    category: PRESET_CATEGORIES.DIGITAL.WEB_DESIGN,
    description: 'Large desktop viewport',
    hasOrientations: false
  },
  {
    baseId: 'web-standard',
    name: 'Web Standard',
    width: 1366,
    height: 768,
    category: PRESET_CATEGORIES.DIGITAL.WEB_DESIGN,
    description: 'Most common laptop resolution',
    hasOrientations: false
  },
  {
    baseId: 'web-laptop',
    name: 'Web Laptop',
    width: 1280,
    height: 720,
    category: PRESET_CATEGORIES.DIGITAL.WEB_DESIGN,
    description: 'Standard laptop viewport',
    hasOrientations: false
  },
  {
    baseId: 'web-tablet',
    name: 'Web Tablet',
    width: 1024,
    height: 768,
    category: PRESET_CATEGORIES.DIGITAL.WEB_DESIGN,
    description: 'Tablet breakpoint',
    hasOrientations: true,
    primaryOrientation: 'landscape' // Web tablet is primarily landscape
  },

  // Mobile Screens (portrait as base)
  {
    baseId: 'iphone-14-pro',
    name: 'iPhone 14 Pro',
    width: 1179,
    height: 2556,
    category: PRESET_CATEGORIES.DIGITAL.MOBILE,
    description: 'iPhone 14 Pro - 393 × 852 pt',
    hasOrientations: true,
    primaryOrientation: 'portrait' // Also generate base ID for primary orientation
  },
  {
    baseId: 'iphone-14',
    name: 'iPhone 14',
    width: 1170,
    height: 2532,
    category: PRESET_CATEGORIES.DIGITAL.MOBILE,
    description: 'iPhone 14 - 390 × 844 pt',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'iphone-se',
    name: 'iPhone SE',
    width: 750,
    height: 1334,
    category: PRESET_CATEGORIES.DIGITAL.MOBILE,
    description: 'iPhone SE - 375 × 667 pt',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'android-large',
    name: 'Android Large',
    width: 1080,
    height: 2340,
    category: PRESET_CATEGORIES.DIGITAL.MOBILE,
    description: 'Common Android flagship',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'android-standard',
    name: 'Android Standard',
    width: 1080,
    height: 1920,
    category: PRESET_CATEGORIES.DIGITAL.MOBILE,
    description: 'Standard Android resolution',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },

  // Tablet Screens (portrait as base, but can rotate)
  {
    baseId: 'ipad-pro-12-9',
    name: 'iPad Pro 12.9"',
    width: 2048,
    height: 2732,
    category: PRESET_CATEGORIES.DIGITAL.TABLET,
    description: 'iPad Pro 12.9-inch',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'ipad-air',
    name: 'iPad Air',
    width: 1640,
    height: 2360,
    category: PRESET_CATEGORIES.DIGITAL.TABLET,
    description: 'iPad Air 10.9-inch',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'ipad-standard',
    name: 'iPad Standard',
    width: 1620,
    height: 2160,
    category: PRESET_CATEGORIES.DIGITAL.TABLET,
    description: 'iPad 10.2-inch',
    hasOrientations: true,
    primaryOrientation: 'portrait'
  },
  {
    baseId: 'surface-pro',
    name: 'Surface Pro',
    width: 2880,
    height: 1920,
    category: PRESET_CATEGORIES.DIGITAL.TABLET,
    description: 'Microsoft Surface Pro',
    hasOrientations: true,
    primaryOrientation: 'landscape'
  }
];

/**
 * Generate preset with specific orientation
 * @param {Object} basePreset - Base preset object
 * @param {string} orientation - 'portrait', 'landscape', or 'base'
 * @returns {Object} Generated preset object
 */
function generatePresetWithOrientation(basePreset, orientation = 'base') {
  const { baseId, name, width, height, category, description, hasOrientations } = basePreset;
  
  if (orientation === 'base' || !hasOrientations) {
    // Return base preset as-is
    return {
      id: baseId,
      name,
      width,
      height,
      category,
      description
    };
  }
  
  // Determine if base is portrait or landscape
  const baseIsPortrait = width < height;
  
  if (orientation === 'portrait') {
    if (baseIsPortrait) {
      // Base is already portrait
      return {
        id: `${baseId}-portrait`,
        name: `${name} Portrait`,
        width,
        height,
        category,
        description
      };
    } else {
      // Swap to make portrait
      return {
        id: `${baseId}-portrait`,
        name: `${name} Portrait`,
        width: height,
        height: width,
        category,
        description
      };
    }
  }
  
  if (orientation === 'landscape') {
    if (!baseIsPortrait) {
      // Base is already landscape
      return {
        id: `${baseId}-landscape`,
        name: `${name} Landscape`,
        width,
        height,
        category,
        description
      };
    } else {
      // Swap to make landscape
      return {
        id: `${baseId}-landscape`,
        name: `${name} Landscape`,
        width: height,
        height: width,
        category,
        description
      };
    }
  }
  
  return null;
}

/**
 * Get all presets for a specific unit type
 * @param {string} unit - 'pt' or 'px'
 * @returns {Array} Array of preset objects
 */
export function getPresetsForUnit(unit) {
  return unit === 'pt' ? POINT_PRESETS : PIXEL_PRESETS;
}

/**
 * Generate all presets from base presets
 */
function generateAllPresets(basePresets) {
  const expandedPresets = [];
  
  basePresets.forEach(basePreset => {
    if (basePreset.hasOrientations) {
      // Add both portrait and landscape versions
      expandedPresets.push(generatePresetWithOrientation(basePreset, 'portrait'));
      expandedPresets.push(generatePresetWithOrientation(basePreset, 'landscape'));
      
      // If has primary orientation, also add base ID without suffix
      if (basePreset.primaryOrientation) {
        const primaryPreset = generatePresetWithOrientation(basePreset, basePreset.primaryOrientation);
        primaryPreset.id = basePreset.baseId; // Use base ID without suffix
        expandedPresets.push(primaryPreset);
      }
    } else {
      // Add only the base version
      expandedPresets.push(generatePresetWithOrientation(basePreset, 'base'));
    }
  });
  
  return expandedPresets;
}

// Generate expanded presets at module load time
export const POINT_PRESETS = generateAllPresets(POINT_BASE_PRESETS);
export const PIXEL_PRESETS = generateAllPresets(PIXEL_BASE_PRESETS);

/**
 * Get presets grouped by category for a specific unit
 * @param {string} unit - 'pt' or 'px' 
 * @returns {Object} Object with categories as keys and preset arrays as values
 */
export function getPresetsByCategory(unit) {
  const presets = getPresetsForUnit(unit);
  const grouped = {};
  
  presets.forEach(preset => {
    if (!grouped[preset.category]) {
      grouped[preset.category] = [];
    }
    grouped[preset.category].push(preset);
  });
  
  return grouped;
}

/**
 * Find a preset by ID
 * @param {string} presetId - The preset ID to find
 * @param {string} unit - 'pt' or 'px'
 * @returns {Object|null} Preset object or null if not found
 */
export function findPresetById(presetId, unit) {
  const presets = getPresetsForUnit(unit);
  return presets.find(preset => preset.id === presetId) || null;
}

/**
 * Get the opposite orientation preset (portrait ↔ landscape)
 * @param {string} presetId - Current preset ID
 * @param {string} unit - 'pt' or 'px'
 * @returns {Object|null} Opposite orientation preset or null
 */
export function getOppositeOrientation(presetId, unit) {
  const presets = getPresetsForUnit(unit);
  
  if (presetId.includes('-portrait')) {
    const landscapeId = presetId.replace('-portrait', '-landscape');
    return presets.find(preset => preset.id === landscapeId) || null;
  }
  
  if (presetId.includes('-landscape')) {
    const portraitId = presetId.replace('-landscape', '-portrait');
    return presets.find(preset => preset.id === portraitId) || null;
  }
  
  return null;
}

/**
 * Check if current dimensions match a preset
 * @param {number} width - Current width
 * @param {number} height - Current height  
 * @param {string} unit - 'pt' or 'px'
 * @returns {Object|null} Matching preset or null
 */
export function findMatchingPreset(width, height, unit) {
  const presets = getPresetsForUnit(unit);
  return presets.find(preset => 
    preset.width === width && preset.height === height
  ) || null;
}